import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useUserControllerGetMe } from '../../api/informApi/informApiComponents';
import { SerializedUser } from '../../api/informApi/informApiSchemas';

export type UserSessionType = {
  currentOrganization?: string;
  isAuthenticating: boolean;
  setToken: Dispatch<SetStateAction<string | null>>;
  refetch: () => Promise<unknown>;
  user: SerializedUser | null;
};

export interface UserSessionProps {
  children: ReactNode;
}

const UserSessionCtx = createContext<UserSessionType>({
  refetch: () => Promise.resolve(),
  isAuthenticating: true,
  setToken: () => {},
  user: null,
});

export function useUserSession() {
  return useContext(UserSessionCtx);
}

function UserSession({ children }: UserSessionProps) {
  const [token, setToken] = useState<string | null>(
    localStorage.getItem('token'),
  );
  const { data, refetch, remove, isLoading, isFetched } =
    useUserControllerGetMe({}, { enabled: false });
  const currentOrganization = data?.memberships?.[0];
  const isAuthenticating = isLoading || !isFetched;

  // For syncing auth with extension
  useEffect(() => {
    if (isAuthenticating) return;
    (window as any)?.chrome?.runtime?.sendMessage(
      process.env.REACT_APP_EXTENSION_ID,
      { orgId: currentOrganization, type: 'SYNC', token },
    );
  }, [token, currentOrganization, isAuthenticating]);

  // Sync localStorage
  useEffect(() => {
    if (token) localStorage.setItem('token', token);
    else localStorage.removeItem('token');
    refetch();

    return () => remove();
  }, [token, remove, refetch]);

  return (
    <UserSessionCtx.Provider
      value={{
        currentOrganization,
        user: data ?? null,
        isAuthenticating,
        setToken,
        refetch,
      }}
    >
      {children}
    </UserSessionCtx.Provider>
  );
}

export default UserSession;
